import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { authValidateResetToken, authResetPassword } from '../api'
import Icon from './Icon'

export default function ResetPasswordScreen({ token, onSuccess, onRequestNew }) {
  const { t } = useTranslation('auth')
  const [valid,    setValid]    = useState(null)  // null=checking, true, false
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [done,     setDone]     = useState(false)
  const [error,    setError]    = useState(null)

  useEffect(() => {
    if (!token) { setValid(false); return }
    authValidateResetToken(token).then(setValid).catch(() => setValid(false))
  }, [token])

  async function handleSubmit(e) {
    e.preventDefault()
    if (password !== confirm) { setError(t('resetPassword.mismatch')); return }
    if (password.length < 8)  { setError(t('resetPassword.tooShort')); return }

    setError(null)
    setLoading(true)
    try {
      const data = await authResetPassword(token, password, confirm)
      setDone(true)
      onSuccess?.(data.user)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Validating token
  if (valid === null) {
    return (
      <div className="full-page">
        <div className="page-body page-body--centered">
          <p className="auth-hint-top" style={{ textAlign: 'center' }}>{t('resetPassword.checking')}</p>
        </div>
      </div>
    )
  }

  // Invalid / expired token
  if (valid === false) {
    return (
      <div className="full-page">
        <div className="page-body page-body--centered">
          <div className="auth-success">
            <p className="auth-success-icon" style={{ color: 'var(--warning)' }}><Icon name="warning-outline" size={40} /></p>
            <p className="auth-success-title">{t('resetPassword.expiredTitle')}</p>
            <p className="auth-success-body">
              {t('resetPassword.expiredBody')}
            </p>
            <button className="modal-submit" onClick={onRequestNew} style={{ marginTop: 24 }}>
              {t('resetPassword.requestNew')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (done) {
    return (
      <div className="full-page">
        <div className="page-body page-body--centered">
          <div className="auth-success">
            <p className="auth-success-icon" style={{ color: 'var(--green)' }}><Icon name="checkmark-circle-outline" size={40} /></p>
            <p className="auth-success-title">{t('resetPassword.doneTitle')}</p>
            <p className="auth-success-body">{t('resetPassword.doneBody')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="full-page">
      <div className="page-header">
        <span className="page-title">{t('resetPassword.title')}</span>
      </div>

      <div className="page-body page-body--centered">
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="modal-field">
            <label className="modal-label">{t('resetPassword.newPassword')}</label>
            <div className="password-field">
              <input
                className="modal-input password-field-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('fields.passwordPlaceholder')}
                required
                autoFocus
              />
              <button
                type="button"
                className="password-field-toggle"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('resetPassword.hide') : t('resetPassword.show')}
                aria-pressed={showPassword}
                tabIndex={-1}
              >
                {showPassword ? <Icon name="eye-off-outline" size={20} /> : <Icon name="eye-outline" size={20} />}
              </button>
            </div>
          </div>

          <div className="modal-field">
            <label className="modal-label">{t('resetPassword.confirm')}</label>
            <div className="password-field">
              <input
                className="modal-input password-field-input"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder={t('resetPassword.confirmPlaceholder')}
                required
              />
              <button
                type="button"
                className="password-field-toggle"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('resetPassword.hide') : t('resetPassword.show')}
                aria-pressed={showPassword}
                tabIndex={-1}
              >
                {showPassword ? <Icon name="eye-off-outline" size={20} /> : <Icon name="eye-outline" size={20} />}
              </button>
            </div>
          </div>

          {error && <p className="auth-error">{error}</p>}

          <button className="modal-submit" type="submit" disabled={loading}>
            {loading ? '...' : t('resetPassword.submit')}
          </button>
        </form>
      </div>
    </div>
  )
}
